import React, { useState } from 'react'
import styled from 'styled-components'
import { OPEN_EVENT } from './AskWidget'

/**
 * The above-the-fold entry point to the ask widget.
 *
 * A corner button is easy to miss, and this is the one thing on the page
 * that answers arbitrary questions — so it gets a real input in the
 * hero. Typing here opens the panel and fires the question immediately.
 *
 * ⭐ Fails closed with the widget: renders nothing unless GATSBY_ASK_URL
 * is set, so the hero never shows a box that goes nowhere.
 */
const ASK_URL = process.env.GATSBY_ASK_URL || ''

const PROMPTS = [
  'What is the Compact?',
  "What's actually built?",
  'Can I run this for my community?',
]

const Bar = styled.div`
  margin-top: 2rem;
  padding: 1.1rem 1.25rem 1.2rem;
  border: 1px solid var(--ink-rule);
  border-left: 3px solid var(--accent);
  border-radius: 10px;
  background: rgba(199, 214, 245, 0.05);

  @media (max-width: 640px) {
    padding: 0.9rem 1rem 1rem;
  }
`

const Lede = styled.div`
  color: var(--chalk);
  font-size: 0.98rem;
  font-weight: 600;
  margin-bottom: 0.2rem;
`

const Sub = styled.div`
  color: var(--chalk-dim);
  font-size: 0.85rem;
  line-height: 1.5;
  margin-bottom: 0.8rem;
`

const Row = styled.form`
  display: flex;
  gap: 0.5rem;
  @media (max-width: 520px) {
    flex-direction: column;
  }
`

const Input = styled.input`
  flex: 1;
  min-width: 0;
  background: var(--ink-deep);
  border: 1px solid var(--ink-rule);
  border-radius: 8px;
  color: var(--chalk);
  font: inherit;
  font-size: 0.95rem;
  padding: 0.62rem 0.8rem;
  &::placeholder {
    color: var(--chalk-dim);
  }
  &:focus {
    outline: none;
    border-color: var(--accent);
  }
`

const Go = styled.button`
  background: var(--card);
  border: none;
  border-radius: 8px;
  color: var(--card-ink);
  font: inherit;
  font-size: 0.95rem;
  font-weight: 600;
  padding: 0.62rem 1.2rem;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: var(--accent-strong);
  }
`

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.7rem;
`

const Chip = styled.button`
  background: none;
  border: 1px solid var(--ink-rule);
  border-radius: 999px;
  color: var(--chalk-dim);
  font: inherit;
  font-size: 0.8rem;
  padding: 0.3rem 0.7rem;
  cursor: pointer;
  &:hover {
    color: var(--chalk);
    border-color: var(--accent);
  }
`

const open = (question) => {
  window.dispatchEvent(
    new CustomEvent(OPEN_EVENT, { detail: question ? { question } : {} })
  )
}

const AskBar = () => {
  const [q, setQ] = useState('')
  if (!ASK_URL) return null

  return (
    <Bar>
      <Lede>Ask it anything about how this works.</Lede>
      <Sub>
        A bot that has read the whole design corpus &mdash; and will tell you
        straight when something is <strong>designed but not built yet</strong>.
        Answers cite the documents they came from.
      </Sub>
      <Row
        onSubmit={(e) => {
          e.preventDefault()
          if (q.trim()) {
            open(q.trim())
            setQ('')
          } else {
            open()
          }
        }}
      >
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="e.g. how is standing earned, and can it be bought?"
          maxLength={2000}
          aria-label="Ask a question about the project"
        />
        <Go type="submit">Ask</Go>
      </Row>
      <Chips>
        {PROMPTS.map((p) => (
          <Chip key={p} onClick={() => open(p)}>
            {p}
          </Chip>
        ))}
      </Chips>
    </Bar>
  )
}

export default AskBar
